// Import Dependencies
import { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useClickOutside } from "hooks";

/**
 * SearchableSelect component provides a searchable dropdown selector
 * matching the CRM filter / selection style with a search bar and checkbox-style options.
 */
export function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  searchPlaceholder = "Search",
  disabled = false,
  required = false,
  className = "",
  style = {},
  error = false,
  id,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close when clicking outside
  useClickOutside(dropdownRef, () => {
    if (isOpen) {
      setIsOpen(false);
      setSearchTerm("");
    }
  });

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small timeout to ensure panel is rendered
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Find the selected option object
  const selectedOption = useMemo(() => {
    if (value === undefined || value === null || value === "") return null;
    return (
      options.find((opt) => String(opt.value) === String(value)) ||
      options.find((opt) => String(opt.label).toLowerCase() === String(value).toLowerCase()) ||
      null
    );
  }, [options, value]);

  const displayLabel = selectedOption ? selectedOption.label : "";

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase().trim();
    return options.filter((opt) =>
      (opt.label || "").toLowerCase().includes(term)
    );
  }, [options, searchTerm]);

  const handleSelect = (val) => {
    onChange?.(val);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.("");
    setSearchTerm("");
  };

  return (
    <div className={clsx("relative w-full", className)} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setIsOpen((prev) => !prev);
        }}
        className={clsx(
          "flex w-full items-center justify-between text-left text-sm transition-all duration-150 cursor-pointer",
          "border bg-white px-3 py-2.5 min-h-[42px] select-none text-gray-800",
          error
            ? "border-red-500 ring-1 ring-red-500/20"
            : isOpen
            ? "border-blue-500 ring-2 ring-blue-500/20"
            : "border-gray-300 hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
          disabled && "cursor-not-allowed opacity-60 bg-gray-50",
          className
        )}
        style={{
          borderRadius: style?.borderRadius || "8px",
          backgroundColor: "#ffffff",
          color: "#1f2937",
          colorScheme: "light",
          ...style,
        }}
      >
        <span
          className={clsx(
            "truncate pr-2 font-normal",
            !selectedOption ? "text-gray-400" : "text-gray-800"
          )}
        >
          {displayLabel || placeholder}
        </span>

        <div className="flex items-center gap-1.5 shrink-0">
          {selectedOption && !disabled && (
            <span
              role="button"
              onClick={handleClear}
              title="Clear selection"
              className="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="size-4" />
            </span>
          )}
          <ChevronDownIcon
            className={clsx(
              "size-4 text-gray-400 transition-transform duration-200",
              isOpen && "rotate-180 text-blue-600"
            )}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute left-0 right-0 top-full z-[100] mt-1.5 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl animate-fadeIn"
          style={{ backgroundColor: "#ffffff", color: "#1f2937", colorScheme: "light" }}
        >
          {/* Search Box matching the second image */}
          <div className="border-b border-gray-100 p-2.5 bg-white">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-8 text-xs sm:text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ backgroundColor: "#ffffff", color: "#1f2937" }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setIsOpen(false);
                    setSearchTerm("");
                  } else if (e.key === "Enter" && filteredOptions.length > 0) {
                    e.preventDefault();
                    handleSelect(filteredOptions[0].value);
                  }
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="size-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto py-1 custom-scrollbar bg-white">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs sm:text-sm text-gray-400">
                No options found
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selectedOption && String(selectedOption.value) === String(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelect(opt.value)}
                    className={clsx(
                      "flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-xs sm:text-sm transition-colors cursor-pointer",
                      "hover:bg-gray-100/70",
                      isSelected
                        ? "bg-blue-50/80 text-blue-700 font-medium"
                        : "text-gray-800"
                    )}
                  >
                    {/* Checkbox indicator matching the second image */}
                    <div
                      className={clsx(
                        "flex size-4 shrink-0 items-center justify-center rounded border transition-colors",
                        isSelected
                          ? "border-blue-600 bg-blue-600 text-white"
                          : "border-gray-300 bg-white"
                      )}
                    >
                      {isSelected && (
                        <CheckIcon className="size-3 stroke-[3]" />
                      )}
                    </div>
                    <span className="truncate leading-relaxed">{opt.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

SearchableSelect.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.node,
    })
  ),
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
  id: PropTypes.string,
};
